import React from "react"
import { Theme, makeStyles } from "@material-ui/core"
import { useTranslation } from "react-i18next"

import ChatHeader from "./ChatHeader"
import ChatMessages from "./ChatMessages"
import MessageComposer from "./MessageComposer"
import { useAppContext } from "../../app-context/use-app-context"
import { ChatResponse } from "../api/chat-response"
import { MessageViewModel } from "../api/message"
import { getSubtitle } from "../utils/chatUtil"

const ChatWindow = ({
  chats,
  chatId,
  embedded = false,
  onBack,
}: {
  chats: ChatResponse[]
  chatId: string | null
  embedded?: boolean
  onBack?: () => void
}) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const { appContext } = useAppContext()
  const { mainContextList, mainContext } = appContext
  const currentUserId = mainContextList.find((ctx) => ctx.type === "user")?.id
  const selectedChat = React.useMemo(() => chats.find((c) => c.id === chatId), [chats, chatId])
  // The chat's own org, not the app context's: a project or task page can be open without an
  // org main context, and then there is nobody to list and nowhere to upload to.
  const orgId = selectedChat?.orgId ?? mainContext?.id
  const [replyTo, setReplyTo] = React.useState<MessageViewModel | null>(null)
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [sentCount, setSentCount] = React.useState(0)

  React.useEffect(() => {
    setReplyTo(null)
    setSearchOpen(false)
  }, [chatId])

  if (!currentUserId) {
    return <div className={classes.emptyState}>{t("chatWindow.noUserContextMessage")}</div>
  }

  return (
    <div className={classes.root}>
      <ChatHeader
        title={selectedChat?.name}
        subtitle={getSubtitle(selectedChat?.type as string, t)}
        selectedChat={selectedChat}
        orgId={orgId}
        embedded={embedded}
        onBack={onBack}
        searchOpen={searchOpen}
        onToggleSearch={() => setSearchOpen((open) => !open)}
      />

      <div className={classes.messages}>
        {chatId ? (
          <ChatMessages
            chatId={chatId}
            searchOpen={searchOpen}
            sentCount={sentCount}
            onCloseSearch={() => setSearchOpen(false)}
            onReply={setReplyTo}
          />
        ) : (
          <div className={classes.welcome}>
            <div className={classes.welcomeIcon}>💬</div>

            <h2>{t("chatWindow.welcomeTitle")}</h2>

            <p>{t("chatWindow.welcomeDescription")}</p>
          </div>
        )}
      </div>

      {chatId && (
        <div className={classes.composer}>
          <MessageComposer
            chatId={chatId}
            senderId={currentUserId}
            orgId={orgId!}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            onSent={() => setSentCount((count) => count + 1)}
          />
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flex: 1,
    // Without this a long message or attachment stretches the flex item past the viewport.
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },

  welcome: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    textAlign: "center",

    "& h2": {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      color: theme.palette.text.primary,
      fontWeight: 500,
    },

    "& p": {
      maxWidth: 320,
      lineHeight: 1.5,
    },
  },

  welcomeIcon: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 40,
    background: theme.palette.action.hover,
  },

  messages: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },

  composer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },

  emptyState: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
  },
}))

export default ChatWindow
